// src/redux/slices/treatmentPlan.js
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  CreateFolderRequest,
  CreateTreatmentPlanRequest,
  StepsHierarchy,
  TreatmentPlanFolder,
  TreatmentPlanResponse,
  DeliveryMethod,
} from '../../api/services/treatmentPlan';
import { ServerTreatmentPlanStep } from '../../api/types/treatmentPlanStep';

interface SendNotificationPayload {
  treatmentPlanId: number;
  patientIds: number[];
  deliveryMethod: DeliveryMethod;
}

interface CurrentTreatmentPlanState {
  id: number | null;
  name: string;
  steps: ServerTreatmentPlanStep[];
  selectedStepId: number | null;
  lastSaved: string | null;
  isSaving: boolean;
  error: string | null;
  isTemplateLoaded: boolean;
  isDraftLoaded: boolean;
  draftId: number | null;
}

interface TreatmentPlanState {
  stepsTree: {
    data: StepsHierarchy | null;
    loading: boolean;
    error: string | null;
  };
  treatmentPlansTree: {
    data: TreatmentPlanFolder | null;
    loading: boolean;
    error: string | null;
  };
  createStep: {
    loading: boolean;
    error: string | null;
  };
  createFolder: {
    loading: boolean;
    error: string | null;
  };
  createTreatmentPlan: {
    loading: boolean;
    error: string | null;
  };
  sentToPatientsIds: string[];
  currentTreatmentPlan: CurrentTreatmentPlanState;
  serverTreatmentPlan: TreatmentPlanResponse | null;
  sendingNotification: boolean;
  sendNotificationError: string | null;
  sendNotificationSuccess: boolean;
  transcription: {
    loading: boolean;
    error: string | null;
    transcript: string;
  };
  transcribeToPlanStatus: {
    loading: boolean;
    error: string | null;
    success: boolean;
  };
}
const initialCurrentTreatmentPlan: CurrentTreatmentPlanState = {
  id: null,
  name: 'Untitled treatment plan',
  steps: [],
  selectedStepId: null,
  lastSaved: null,
  isSaving: false,
  error: null,
  isTemplateLoaded: false,
  isDraftLoaded: false,
  draftId: null,
};
// Initial state
const initialState: TreatmentPlanState = {
  stepsTree: {
    data: null,
    loading: false,
    error: null,
  },
  treatmentPlansTree: {
    data: null,
    loading: false,
    error: null,
  },
  createStep: {
    loading: false,
    error: null,
  },
  createFolder: {
    loading: false,
    error: null,
  },
  createTreatmentPlan: {
    loading: false,
    error: null,
  },
  sentToPatientsIds: [],
  currentTreatmentPlan: initialCurrentTreatmentPlan,
  serverTreatmentPlan: null,
  sendingNotification: false,
  sendNotificationError: null,
  sendNotificationSuccess: false,
  transcription: {
    loading: false,
    error: null,
    transcript: '',
  },
  transcribeToPlanStatus: {
    loading: false,
    error: null,
    success: false,
  },
};

const treatmentPlanSlice = createSlice({
  name: 'treatmentPlan',
  initialState,
  reducers: {
    fetchStepsTree: state => {
      state.stepsTree.loading = true;
      state.stepsTree.error = null;
    },
    fetchStepsTreeSuccess: (state, action: PayloadAction<StepsHierarchy>) => {
      state.stepsTree.data = action.payload;
      state.stepsTree.loading = false;
      state.stepsTree.error = null;
    },
    fetchStepsTreeFailure: (state, action: PayloadAction<string>) => {
      state.stepsTree.loading = false;
      state.stepsTree.error = action.payload;
    },
    fetchTreatmentPlansTree: state => {
      state.treatmentPlansTree.loading = true;
      state.treatmentPlansTree.error = null;
    },
    fetchTreatmentPlansTreeSuccess: (state, action: PayloadAction<TreatmentPlanFolder>) => {
      state.treatmentPlansTree.data = action.payload;
      state.treatmentPlansTree.loading = false;
      state.treatmentPlansTree.error = null;
    },
    fetchTreatmentPlansTreeFailure: (state, action: PayloadAction<string>) => {
      state.treatmentPlansTree.loading = false;
      state.treatmentPlansTree.error = action.payload;
    },
    createStep: (state, _action: PayloadAction<ServerTreatmentPlanStep>) => {
      state.createStep.loading = true;
      state.createStep.error = null;
    },
    createStepSuccess: state => {
      state.createStep.loading = false;
      state.createStep.error = null;
    },
    createStepFailure: (state, action: PayloadAction<string>) => {
      state.createStep.loading = false;
      state.createStep.error = action.payload;
    },
    createFolder: (
      state,
      _action: PayloadAction<{
        request: CreateFolderRequest;
        fetchTree: 'steps' | 'treatmentPlans';
      }>
    ) => {
      state.createFolder.loading = true;
      state.createFolder.error = null;
    },
    createFolderSuccess: state => {
      state.createFolder.loading = false;
      state.createFolder.error = null;
    },
    createFolderFailure: (state, action: PayloadAction<string>) => {
      state.createFolder.loading = false;
      state.createFolder.error = action.payload;
    },
    createTreatmentPlan: (state, _action: PayloadAction<CreateTreatmentPlanRequest>) => {
      state.createTreatmentPlan.loading = true;
      state.createTreatmentPlan.error = null;
    },
    createTreatmentPlanSuccess: state => {
      state.createTreatmentPlan.loading = false;
      state.createTreatmentPlan.error = null;
    },
    createTreatmentPlanFailure: (state, action: PayloadAction<string>) => {
      state.createTreatmentPlan.loading = false;
      state.createTreatmentPlan.error = action.payload;
    },
    setSentToPatientsIds: (state, action: PayloadAction<string[]>) => {
      state.sentToPatientsIds = action.payload;
    },
    setTreatmentPlanId: (state, action: PayloadAction<number>) => {
      state.currentTreatmentPlan.id = action.payload;
    },
    setTreatmentPlanName: (state, action: PayloadAction<string>) => {
      state.currentTreatmentPlan.name = action.payload;
    },
    setTreatmentPlanSteps: (state, action: PayloadAction<ServerTreatmentPlanStep[]>) => {
      state.currentTreatmentPlan.steps = action.payload;
    },
    updateTreatmentPlanStep: (state, action: PayloadAction<ServerTreatmentPlanStep>) => {
      const stepIndex = state.currentTreatmentPlan.steps.findIndex(s => s.id === action.payload.id);
      if (stepIndex !== -1) {
        state.currentTreatmentPlan.steps[stepIndex] = action.payload;
      }
    },
    addTreatmentPlanStep: (state, action: PayloadAction<ServerTreatmentPlanStep>) => {
      state.currentTreatmentPlan.steps.push(action.payload);
    },
    deleteTreatmentPlanStep: (state, action: PayloadAction<string | number>) => {
      state.currentTreatmentPlan.steps = state.currentTreatmentPlan.steps.filter(
        step => step.id !== action.payload
      );
    },
    setSelectedStepId: (state, action: PayloadAction<number | null>) => {
      state.currentTreatmentPlan.selectedStepId = action.payload;
    },
    setTreatmentPlanSaving: (state, action: PayloadAction<boolean>) => {
      state.currentTreatmentPlan.isSaving = action.payload;
    },
    setTreatmentPlanLastSaved: (state, action: PayloadAction<string | null>) => {
      state.currentTreatmentPlan.lastSaved = action.payload;
    },
    setTreatmentPlanError: (state, action: PayloadAction<string | null>) => {
      state.currentTreatmentPlan.error = action.payload;
    },
    fetchTreatmentPlanById: (state, _action: PayloadAction<number>) => {
      state.currentTreatmentPlan.isSaving = true;
      state.currentTreatmentPlan.error = null;
    },
    fetchTreatmentPlanByIdSuccess: state => {
      state.currentTreatmentPlan.isSaving = false;
      state.currentTreatmentPlan.error = null;
    },
    fetchTreatmentPlanByIdFailure: (state, action: PayloadAction<string>) => {
      state.currentTreatmentPlan.isSaving = false;
      state.currentTreatmentPlan.error = action.payload;
    },
    saveTreatmentPlan: state => {
      state.currentTreatmentPlan.isSaving = true;
      state.currentTreatmentPlan.error = null;
    },
    saveTreatmentPlanSuccess: (state, action: PayloadAction<string>) => {
      state.currentTreatmentPlan.isSaving = false;
      state.currentTreatmentPlan.lastSaved = action.payload;
      state.currentTreatmentPlan.error = null;
    },
    saveTreatmentPlanFailure: (state, action: PayloadAction<string>) => {
      state.currentTreatmentPlan.isSaving = false;
      state.currentTreatmentPlan.error = action.payload;
    },
    resetTreatmentPlan: state => {
      state.currentTreatmentPlan = initialCurrentTreatmentPlan;
    },
    setServerTreatmentPlan: (state, action: PayloadAction<TreatmentPlanResponse>) => {
      state.serverTreatmentPlan = action.payload;
    },
    resetState: state => {
      state.currentTreatmentPlan = initialState.currentTreatmentPlan;
      state.serverTreatmentPlan = initialState.serverTreatmentPlan;
    },
    // Notification actions
    sendNotification: (
      state,
      action: PayloadAction<
        SendNotificationPayload & {
          treatmentPlanId: number;
        }
      >
    ) => {
      state.sendingNotification = true;
      state.sendNotificationError = null;
      state.sendNotificationSuccess = false;
    },
    sendNotificationSuccess: state => {
      state.sendingNotification = false;
      state.sendNotificationSuccess = true;
    },
    sendNotificationFailure: (state, action: PayloadAction<string>) => {
      state.sendingNotification = false;
      state.sendNotificationError = action.payload;
    },
    resetSendNotificationState: state => {
      state.sendingNotification = false;
      state.sendNotificationError = null;
      state.sendNotificationSuccess = false;
    },
    resetCurrentTreatmentPlan: state => {
      state.currentTreatmentPlan = {
        ...initialCurrentTreatmentPlan,
        isTemplateLoaded: false,
        isDraftLoaded: false,
        draftId: null,
      };
    },
    deleteFolder: (
      state,
      _action: PayloadAction<{ id: number; fetchTree: 'steps' | 'treatmentPlans' }>
    ) => {
      state.createFolder.loading = true;
      state.createFolder.error = null;
    },
    deleteFolderSuccess: state => {
      state.createFolder.loading = false;
      state.createFolder.error = null;
    },
    deleteFolderFailure: (state, action: PayloadAction<string>) => {
      state.createFolder.loading = false;
      state.createFolder.error = action.payload;
    },
    deleteTreatmentPlan: (state, _action: PayloadAction<{ id: number }>) => {
      state.createTreatmentPlan.loading = true;
      state.createTreatmentPlan.error = null;
    },
    deleteTreatmentPlanSuccess: state => {
      state.createTreatmentPlan.loading = false;
      state.createTreatmentPlan.error = null;
    },
    deleteTreatmentPlanFailure: (state, action: PayloadAction<string>) => {
      state.createTreatmentPlan.loading = false;
      state.createTreatmentPlan.error = action.payload;
    },
    // Load template action
    loadTemplate: (state, action: PayloadAction<TreatmentPlanResponse>) => {
      const template = action.payload;

      if (template?.id) {
        state.currentTreatmentPlan.id = template.id;
      }
      state.currentTreatmentPlan.name = template.name;
      state.currentTreatmentPlan.steps = template.steps || [];
      state.currentTreatmentPlan.selectedStepId =
        template.steps && template.steps.length > 0 ? (template.steps[0].id as number) : null;
      state.currentTreatmentPlan.id = null; // Reset ID since this is a new plan based on template
      state.currentTreatmentPlan.lastSaved = null;
      state.currentTreatmentPlan.isSaving = false;
      state.currentTreatmentPlan.error = null;
      state.currentTreatmentPlan.isTemplateLoaded = true;
      state.currentTreatmentPlan.isDraftLoaded = false;
      state.currentTreatmentPlan.draftId = null;
    },
    // Load draft action
    loadDraft: (state, action: PayloadAction<TreatmentPlanResponse>) => {
      const draft = action.payload;

      state.currentTreatmentPlan.id = null; // Reset ID since we're editing a draft
      state.currentTreatmentPlan.name = draft.name;
      state.currentTreatmentPlan.steps = draft.steps || [];
      state.currentTreatmentPlan.selectedStepId =
        draft.steps && draft.steps.length > 0 ? (draft.steps[0].id as number) : null;
      state.currentTreatmentPlan.lastSaved = null;
      state.currentTreatmentPlan.isSaving = false;
      state.currentTreatmentPlan.error = null;
      state.currentTreatmentPlan.isTemplateLoaded = false;
      state.currentTreatmentPlan.isDraftLoaded = true;
      state.currentTreatmentPlan.draftId = draft.id;

      // Set patient if assigned to the draft
      if (draft.patient) {
        state.sentToPatientsIds = [draft.patient.toString()];
      } else {
        state.sentToPatientsIds = [];
      }
    },
    transcribeAudio: (state, _action: PayloadAction<Blob>) => {
      state.transcription = { loading: true, error: null, transcript: '' };
    },
    transcribeAudioSuccess: (state, action: PayloadAction<string>) => {
      state.transcription = { loading: false, error: null, transcript: action.payload };
    },
    transcribeAudioFailure: (state, action: PayloadAction<string>) => {
      state.transcription = { loading: false, error: action.payload, transcript: '' };
    },
    setTranscript: (state, action: PayloadAction<string>) => {
      state.transcription = { ...state.transcription, transcript: action.payload };
    },
    transcribeToPlan: (state, _action: PayloadAction<string>) => {
      state.transcribeToPlanStatus = { loading: true, error: null, success: false };
    },
    transcribeToPlanSuccess: state => {
      state.transcribeToPlanStatus = { loading: false, error: null, success: true };
    },
    transcribeToPlanFailure: (state, action: PayloadAction<string>) => {
      state.transcribeToPlanStatus = { loading: false, error: action.payload, success: false };
    },
    resetTranscribeToPlanStatus: state => {
      state.transcribeToPlanStatus = { loading: false, error: null, success: false };
    },
    // Save treatment plan as draft
    saveAsDraft: (state, _action: PayloadAction<{ draftName: string }>) => {
      state.currentTreatmentPlan.isSaving = true;
      state.currentTreatmentPlan.error = null;
    },
    saveAsDraftSuccess: (state, action: PayloadAction<TreatmentPlanResponse>) => {
      state.currentTreatmentPlan.isSaving = false;
      state.currentTreatmentPlan.error = null;
      state.currentTreatmentPlan.id = action.payload.id;
      state.currentTreatmentPlan.lastSaved = new Date().toISOString();
    },
    saveAsDraftFailure: (state, action: PayloadAction<string>) => {
      state.currentTreatmentPlan.isSaving = false;
      state.currentTreatmentPlan.error = action.payload;
    },
    // Update existing draft
    updateDraft: (state, _action: PayloadAction<{ id: number; draftName: string }>) => {
      state.currentTreatmentPlan.isSaving = true;
      state.currentTreatmentPlan.error = null;
    },
    updateDraftSuccess: (state, action: PayloadAction<TreatmentPlanResponse>) => {
      state.currentTreatmentPlan.isSaving = false;
      state.currentTreatmentPlan.error = null;
      state.currentTreatmentPlan.lastSaved = new Date().toISOString();
    },
    updateDraftFailure: (state, action: PayloadAction<string>) => {
      state.currentTreatmentPlan.isSaving = false;
      state.currentTreatmentPlan.error = action.payload;
    },
  },
});

export const {
  fetchStepsTree,
  fetchStepsTreeSuccess,
  fetchStepsTreeFailure,
  fetchTreatmentPlansTree,
  fetchTreatmentPlansTreeSuccess,
  fetchTreatmentPlansTreeFailure,
  createStep,
  createStepSuccess,
  createStepFailure,
  createFolder,
  createFolderSuccess,
  createFolderFailure,
  createTreatmentPlan,
  createTreatmentPlanSuccess,
  createTreatmentPlanFailure,
  setSentToPatientsIds,
  setTreatmentPlanId,
  setTreatmentPlanName,
  setTreatmentPlanSteps,
  updateTreatmentPlanStep,
  addTreatmentPlanStep,
  deleteTreatmentPlanStep,
  setSelectedStepId,
  setTreatmentPlanSaving,
  setTreatmentPlanLastSaved,
  setTreatmentPlanError,
  fetchTreatmentPlanById,
  fetchTreatmentPlanByIdSuccess,
  fetchTreatmentPlanByIdFailure,
  saveTreatmentPlan,
  saveTreatmentPlanSuccess,
  saveTreatmentPlanFailure,
  resetTreatmentPlan,
  setServerTreatmentPlan,
  resetState,
  sendNotification,
  sendNotificationSuccess,
  sendNotificationFailure,
  resetSendNotificationState,
  resetCurrentTreatmentPlan,
  deleteFolder,
  deleteFolderSuccess,
  deleteFolderFailure,
  deleteTreatmentPlan,
  deleteTreatmentPlanSuccess,
  deleteTreatmentPlanFailure,
  loadTemplate,
  loadDraft,
  transcribeAudio,
  transcribeAudioSuccess,
  transcribeAudioFailure,
  setTranscript,
  transcribeToPlan,
  transcribeToPlanSuccess,
  transcribeToPlanFailure,
  resetTranscribeToPlanStatus,
  saveAsDraft,
  saveAsDraftSuccess,
  saveAsDraftFailure,
  updateDraft,
  updateDraftSuccess,
  updateDraftFailure,
} = treatmentPlanSlice.actions;

export default treatmentPlanSlice.reducer;
